import React, { useState, useEffect } from "react";
import {
  Dropdown,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import Select, { components } from "react-select";
import { useSearchParams, useNavigate } from "react-router-dom";
import RetreatRoasterApi from "../../../api/retreatRoasterApi";
import RetreatApi from "../../../api/retreatApi";
import AuthApi from "../../../api/authApi";
import CustomDataTable from "../../../components/CustomDataTable";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ArrowLeft, CalendarDays, Download, Users } from "lucide-react";
import "../../../assets/css/buildMyTeam.css";

const BuildMyTeam = () => {
  const [searchParams] = useSearchParams();
  const retreatId = searchParams.get("retreatId");
  const isTemp = searchParams.get("isTemp") === "true";
  const navigate = useNavigate();
  const retreatRoastApi = new RetreatRoasterApi();
  const retreatApi = new RetreatApi();
  const authApi = new AuthApi();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [retreatDetail, setRetreatDetail] = useState(null);
  const [parishOptions, setParishOptions] = useState([]);

  // Filter States
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Array of strings: ['English', 'Spanish']
  const [selectedParishes, setSelectedParishes] = useState([]); // Array of {value, label}
  const [filterOption, setFilterOption] = useState("neverTeamed");

  const filterOptions = [
    {
      value: "neverTeamed",
      label: "Couples who have NEVER teamed from my Parish",
    },
    { value: "neverTeamedBefore", label: "Couples who NEVER teamed before" },
    {
      value: "teamedPast5Years",
      label: "Couples who have teamed in the past 5 years",
    },
    { value: "teamedBefore", label: "Couples who have teamed before" },
    { value: "headCouple", label: "Couples who have served as a Head Couple" },
    { value: "givenTalk", label: "Couples who have given a Talk" },
    { value: "allAttendeeTypes", label: "All Attendee Types" },
  ];

  const languageOptions = ["English", "Spanish"];
  const CustomOption = (props) => {
    return (
      <div>
        <components.Option {...props}>
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              checked={props.isSelected}
              onChange={() => null}
              className="form-check-input me-3"
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: props.isSelected ? "600" : "400",
              }}
            >
              {props.label}
            </span>
          </div>
        </components.Option>
      </div>
    );
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "12px",
      padding: "6px 8px",
      border: state.isFocused ? "1px solid #1f3bb3" : "1px solid #dee2e6",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(31, 59, 179, 0.1)" : "none",
      transition: "all 0.3s ease",
      "&:hover": {
        border: "1px solid #1f3bb3",
      },
      backgroundColor: "#fff",
      cursor: "pointer",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#adb5bd",
      fontSize: "0.95rem",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#212529",
      fontWeight: "500",
      fontSize: "0.95rem",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#eef2ff",
      borderRadius: "6px",
      padding: "2px 4px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1f3bb3",
      fontWeight: "600",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#1f3bb3",
      "&:hover": {
        backgroundColor: "#1f3bb3",
        color: "#fff",
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#1f3bb3",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      border: "1px solid #f1f3f5",
      padding: "8px",
      zIndex: 100,
      marginTop: "8px",
      animation: "fadeIn 0.2s ease-out",
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: "8px",
      backgroundColor: state.isSelected
        ? "#f0f2ff"
        : state.isFocused
          ? "#f8f9fa"
          : "transparent",
      color: state.isSelected ? "#1f3bb3" : "#495057",
      cursor: "pointer",
      fontWeight: state.isSelected ? "600" : "400",
      padding: "10px 12px",
      "&:active": {
        backgroundColor: "#e8ebff",
      },
    }),
  };

  useEffect(() => {
    fetchParishes();
  }, []);

  useEffect(() => {
    if (retreatId) {
      fetchRetreatDetails();
    }
  }, [retreatId, isTemp]);

  const fetchParishes = async () => {
    try {
      const res = await authApi.getPerishList();
      if (res.success) {
        const sortedParishes = res.data
          .sort((a, b) => (a.parish || "").localeCompare(b.parish || ""))
          .map((p) => ({
            value: p.id,
            label: p.parish,
          }));
        setParishOptions(sortedParishes);
      }
    } catch (error) {
      console.error("Error fetching parishes", error);
    }
  };

  const fetchRetreatDetails = async () => {
    try {
      const res = await retreatApi.getRetreatDetail({ id: retreatId, isTemp });
      if (res.success && res.data && res.data.length > 0) {
        const detail = res.data[0];
        setRetreatDetail(detail);
        // Default Behavior: Auto-select current retreat language and parish
        if (detail.language) {
          setSelectedLanguages([capitalize(detail.language)]);
        }
        if (detail.parishId) {
          const pName =
            detail.myparish?.parish || detail.parishName || "My Parish";
          setSelectedParishes([{ value: detail.parishId, label: pName }]);
        }
      }
    } catch (error) {
      console.error("Error fetching retreat details", error);
    }
  };

  useEffect(() => {
    // Only fetch if we have at least one language (parish can be empty for global search)
    if (retreatId && selectedLanguages.length > 0) {
      fetchData();
    }
  }, [selectedLanguages, selectedParishes, filterOption]);

  const fetchData = async (
    pageNo = 1,
    size = 100,
    searchKeyword = null,
    sortBy = {},
  ) => {
    setLoading(true);
    try {
      const parishIds = selectedParishes.some((p) => p.value === "all")
        ? ["all"]
        : selectedParishes.map((p) => p.value);

      const res = await retreatRoastApi.buildMyTeam({
        retreatId: retreatId,
        languages: selectedLanguages,
        parishIds: parishIds,
        filterOption: filterOption,
        sortBy: sortBy,
      });

      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.msg);
      }
    } catch (error) {
      toast.error("Error fetching data");
    }
    setLoading(false);
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );
  };

  const handleParishChange = (selected) => {
    if (selected && selected.some((p) => p.value === "all")) {
      setSelectedParishes([{ value: "all", label: "Select All Parishes" }]);
    } else if (selected && selected.some((p) => p.value === "none")) {
      setSelectedParishes([]);
    } else {
      setSelectedParishes(selected || []);
    }
  };

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip">{props}</Tooltip>
  );

  const columns = [
    {
      id: "retreatName",
      name: "Retreat Name",
      selector: (row) => row.retreatName,
      sortable: true,
      width: "280px",
      cell: (row) => {
        const fullName = `${row?.retreatParishName}${row.language ? ` (${capitalize(row.language)})` : ""} ${row.location ? ` ${capitalize(row.location)}` : ""}`;
        const displayDate = row.retreatDate
          ? moment(row.retreatDate).format("MMM DD, YYYY")
          : "";
        return (
          <div className="py-2">
            <OverlayTrigger
              placement="bottom"
              overlay={renderTooltip(fullName)}
            >
              <a
                href={`/retreatRoster?retreatId=${row.retreatId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    `/retreatRoster?retreatId=${row.retreatId}`,
                    "_blank",
                  );
                }}
                className="d-block text-truncate fw-bold"
                style={{ maxWidth: "260px", color: "#1f3bb3" }}
              >
                {fullName}
              </a>
            </OverlayTrigger>
            {displayDate && (
              <div className="small text-muted">{displayDate}</div>
            )}
          </div>
        );
      },
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      selector: (row) => row.retreatId,
      sortable: true,
      width: "120px",
    },
    {
      id: "attendeeType",
      name: "Attendee Type",
      selector: (row) => row.attendeeType,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span
          className={`badge ${row.attendeeType === "Team" ? "bg-success" : "bg-info text-dark"} rounded-pill`}
        >
          {row.attendeeType}
        </span>
      ),
    },

    {
      id: "hisLastName",
      name: "His Last Name",
      selector: (row) => row.hisLastName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisLastName)}
        >
          <span className="text-truncate">{row.hisLastName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisFirstName",
      name: "His First Name",
      selector: (row) => row.hisFirstName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisFirstName)}
        >
          <span className="text-truncate">{row.hisFirstName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisEmail",
      name: "His Email",
      selector: (row) => row.hisEmail,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisEmail)}
        >
          <span className="text-truncate">{row.hisEmail}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisMobile",
      name: "His Mobile",
      selector: (row) => row.hisMobile,
      sortable: true,
      width: "150px",
    },
    {
      id: "herLastName",
      name: "Her Last Name",
      selector: (row) => row.herLastName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herLastName)}
        >
          <span className="text-truncate">{row.herLastName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herFirstName",
      name: "Her First Name",
      selector: (row) => row.herFirstName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herFirstName)}
        >
          <span className="text-truncate">{row.herFirstName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herEmail",
      name: "Her Email",
      selector: (row) => row.herEmail,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herEmail)}
        >
          <span className="text-truncate">{row.herEmail}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herMobile",
      name: "Her Mobile",
      selector: (row) => row.herMobile,
      sortable: true,
      width: "150px",
    },

    // {
    //   id: "retreatParishName",
    //   name: "Retreat Parish",
    //   selector: (row) => row.retreatParishName,
    //   sortable: true,
    //   width: "200px",
    //   cell: (row) => (
    //     <OverlayTrigger
    //       placement="bottom"
    //       overlay={renderTooltip(row.retreatParishName)}
    //     >
    //       <span className="text-truncate">{row.retreatParishName}</span>
    //     </OverlayTrigger>
    //   ),
    // },
    {
      id: "countRetreatant",
      name: "# Retreatant Couples",
      selector: (row) => row && row.countRetreatant,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="tooltip-static-demo">
          <div
            className="bs-tooltip-bottom bs-tooltip-bottom-demo tooltip-info"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
          >
            <div className="arrow"></div>
            <a
              href={`/retreatRoster?retreatId=${row.retreatId}&flag=1`}
              style={{ textDecoration: "none" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="tooltip-inner">{row && row.countRetreatant}</div>
            </a>
          </div>
        </div>
      ),
    },
    {
      id: "countTeam",
      name: "# Team Couples",
      selector: (row) => row && row.countTeam,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div className="tooltip-static-demo">
          <div
            className="bs-tooltip-bottom bs-tooltip-bottom-demo tooltip-info"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
          >
            <div className="arrow"></div>
            <a
              href={`/retreatRoster?retreatId=${row.retreatId}&flag=2`}
              style={{ textDecoration: "none" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="tooltip-inner">{row && row.countTeam}</div>
            </a>
          </div>
        </div>
      ),
    },
    {
      id: "language",
      name: "Language",
      selector: (row) => row.language,
      sortable: true,
      width: "120px",
      cell: (row) => capitalize(row.language),
    },
    {
      id: "parishName",
      name: "Couple Parish",
      selector: (row) => row.parishName,
      sortable: true,
      width: "200px",
    },
    {
      id: "roleServed",
      name: "Role Served",
      selector: (row) => row.roleServed,
      sortable: true,
      width: "150px",
    },
    {
      id: "serviceTitle",
      name: "Service Title",
      selector: (row) => row.service,
      sortable: true,
      width: "150px",
    },
    {
      id: "talkGiven",
      name: "Talk Given",
      selector: (row) => row.talkGiven,
      sortable: true,
      width: "150px",
    },
  ];

  const formatRetreatName = (item) => {
    return `${item?.retreatParishName || ""}${item.language ? ` (${capitalize(item.language)})` : ""} ${item.location ? ` ${capitalize(item.location)}` : ""}`.trim();
  };

  const getExportCellValue = (column, item) => {
    switch (column.id) {
      case "retreatName": {
        const name = formatRetreatName(item);
        const date = item.retreatDate
          ? moment(item.retreatDate).format("MMM DD, YYYY")
          : "";
        return date ? `${name}\n${date}` : name;
      }
      case "language":
        return capitalize(item.language) || "";
      case "serviceTitle":
        return item.service || "";
      default:
        return column.selector(item) ?? "";
    }
  };

  const exportHeaders = columns.map((column) => column.name);

  const exportToExcel = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(
      data.map((item) => {
        const row = {};
        columns.forEach((column) => {
          row[column.name] = getExportCellValue(column, item);
        });
        return row;
      }),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TeamMembers");
    XLSX.writeFile(wb, "BuildMyTeam.xlsx");
  };

  const exportToPDF = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    const doc = new jsPDF("landscape", "pt", "a4");

    doc.setFontSize(16);
    doc.setTextColor(31, 59, 179);
    doc.text("Build My Team Report", 14, 25);

    const tableData = data.map((item) =>
      columns.map((column) => getExportCellValue(column, item)),
    );

    doc.autoTable({
      head: [exportHeaders],
      body: tableData,
      startY: 35,
      theme: "grid",
      styles: {
        fontSize: 5.5,
        cellPadding: 2,
        overflow: "linebreak",
        halign: "left",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [31, 59, 179],
        textColor: 255,
        fontSize: 6,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: 35, right: 10, bottom: 20, left: 10 },
    });

    doc.save("BuildMyTeam.pdf");
  };

  const activeFilterLabel =
    filterOptions.find((opt) => opt.value === filterOption)?.label || "";

  return (
    <div className="bmt-page">
      <header className="bmt-header">
        <div>
          <button type="button" className="bmt-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Back
          </button>
          <p className="bmt-kicker">Retreat roster</p>
          <h1>Build My Team</h1>
          <p>
            Search Couples by Language, Parish, and the ways they have already
            served.
          </p>
        </div>
        <Dropdown className="d-inline-block">
          <Dropdown.Toggle className="bmt-export" id="dropdown-export">
            <Download size={16} />
            Export
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            <Dropdown.Item onClick={exportToExcel}>Export to Excel</Dropdown.Item>
            <Dropdown.Item onClick={exportToPDF}>Export to PDF</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </header>

      {retreatDetail ? (
        <section className="bmt-identity">
          <div className="bmt-identity-icon" aria-hidden="true">
            <Users size={20} />
          </div>
          <div>
            <h2>{retreatDetail.title}</h2>
            <div className="bmt-chips">
              <span className="bmt-chip">
                <CalendarDays size={14} />
                {retreatDetail.dateFrom
                  ? moment(retreatDetail.dateFrom).format("MMM DD, YYYY")
                  : "N/A"}
              </span>
              <a
                href={`/registration-retreat/${retreatDetail.id}`}
                target="_blank"
                rel="noreferrer"
                title="Retreat registration link"
                className="bmt-chip"
              >
                Retreat ID: {retreatDetail.id}
              </a>
              <span className="bmt-chip">
                Parish ID:{" "}
                {retreatDetail.myparish?.uniqueId ||
                  retreatDetail.parish?.uniqueId ||
                  retreatDetail.parishId ||
                  "N/A"}
              </span>
            </div>
          </div>
        </section>
      ) : (
        <div className="bmt-skeleton" aria-hidden="true" />
      )}

      <section className="bmt-filters">
        <div className="bmt-filters-head">
          <h2>Find couples</h2>
          <p>Results update as you change these filters.</p>
        </div>
        <div className="bmt-grid">
          <div>
            <span className="bmt-label">Language</span>
            <div className="bmt-languages">
              {languageOptions.map((lang) => (
                <label
                  key={lang}
                  className={`bmt-lang${
                    selectedLanguages.includes(lang) ? " is-on" : ""
                  }`}
                  htmlFor={`check-${lang}`}
                >
                  <input
                    type="checkbox"
                    id={`check-${lang}`}
                    checked={selectedLanguages.includes(lang)}
                    onChange={() => handleLanguageChange(lang)}
                  />
                  {lang}
                </label>
              ))}
            </div>
          </div>

          <div>
            <span className="bmt-label">Parishes</span>
            <Select
              isMulti
              options={[
                { value: "all", label: "Select All Parishes" },
                { value: "none", label: "Deselect All Parishes" },
                ...parishOptions,
              ]}
              value={selectedParishes}
              onChange={handleParishChange}
              placeholder="Select one or multiple parishes..."
              className="react-select-container"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option: CustomOption }}
              styles={{
                ...customSelectStyles,
                control: (base, state) => ({
                  ...customSelectStyles.control(base, state),
                  maxHeight: "150px",
                  overflowY: "auto",
                }),
                valueContainer: (base) => ({
                  ...base,
                  maxHeight: "140px",
                  overflowY: "auto",
                }),
              }}
            />
            <div className="bmt-links">
              <button
                type="button"
                className="bmt-link"
                onClick={() => handleParishChange(parishOptions)}
              >
                Select all
              </button>
              <button
                type="button"
                className="bmt-link is-muted"
                onClick={() => handleParishChange([])}
              >
                Deselect all
              </button>
            </div>
          </div>

          <div>
            <span className="bmt-label">Filter</span>
            <Select
              options={filterOptions}
              value={filterOptions.find((opt) => opt.value === filterOption)}
              onChange={(opt) => {
                if (opt) setFilterOption(opt.value);
              }}
              placeholder="Select a filter..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
            />
          </div>
        </div>
      </section>

      <div className="bmt-results">
        <strong>
          {data.length} {data.length === 1 ? "couple" : "couples"}
        </strong>
        <span>{activeFilterLabel}</span>
      </div>
      {selectedLanguages.length === 0 && data.length === 0 && !loading ? (
        <p className="bmt-note">Select at least one language to search.</p>
      ) : null}

      <div className="bmt-table-card">
        <CustomDataTable
          columns={columns}
          data={data}
          loading={loading}
          pagination={true}
          highlightOnHover={true}
          pointerOnHover={false}
          sortServer={true}
          dataFetchApi={fetchData}
        />
      </div>
    </div>
  );
};

export default BuildMyTeam;
